// Inline SVGs for the reward screens. Inline rather than an icon package
// because these sit on the signup-adjacent surface, where an extra dependency
// would load for every visitor, and because the brand marks (G2, Capterra,
// Play, Product Hunt, Trustpilot) have no package that carries them all.

const stroke = (props = {}) => ({
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  ...props,
});

export const BackIcon = (p) => (
  <svg {...stroke({ width: 18, height: 18, ...p })}>
    <path d="M15 5l-7 7 7 7" />
  </svg>
);

export const InfoIcon = (p) => (
  <svg {...stroke({ width: 18, height: 18, ...p })}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 7.6h.01" />
  </svg>
);

export const ChevronIcon = (p) => (
  <svg {...stroke({ width: 15, height: 15, strokeWidth: 2.2, ...p })}>
    <path d="M9 5l7 7-7 7" />
  </svg>
);

export const ClockIcon = (p) => (
  <svg {...stroke(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

// The Earned Minutes badge, exactly as drawn: a 36px lilac disc with the clock
// inside it. Self-contained (the disc is part of the artwork, not a CSS
// background) so it cannot drift from the design, which is why it does not
// reuse ClockIcon — that one is a bare stroke glyph used at three other sizes.
export const EarnedClockIcon = (p) => (
  <svg viewBox="0 0 36 36" width="36" height="36" fill="none" aria-hidden="true" {...p}>
    <circle cx="18" cy="18" r="18" fill="#F0EAFC" />
    <path
      d="M18.0004 11.3291C17.6828 11.3291 17.4253 11.5866 17.4253 11.9042V18.0005C17.4253 18.2246 17.5535 18.4196 17.7408 18.5143H17.7397L21.2583 20.5461C21.3486 20.5985 21.4478 20.623 21.5448 20.623C21.7432 20.623 21.9371 20.5193 22.043 20.3355C22.2024 20.0602 22.1076 19.708 21.8334 19.5497L18.5756 17.6684V11.9042C18.5756 11.5865 18.3181 11.3291 18.0004 11.3291ZM18.0004 7.69704C23.6913 7.69704 28.3045 12.31 28.3045 17.9995V18.0017C28.3045 23.6912 23.6913 28.3042 18.0004 28.3042C12.3096 28.3042 7.69635 23.6912 7.69635 18.0017V17.9995C7.69635 12.31 12.3096 7.69704 18.0004 7.69704ZM18.0004 6.54688C11.6741 6.54688 6.5459 11.6749 6.5459 17.9997V18.002C6.5459 24.3268 11.6741 29.456 18.0004 29.456C24.3268 29.456 29.455 24.3268 29.455 18.002V17.9997C29.455 11.6749 24.3268 6.54688 18.0004 6.54688Z"
      fill="#6524EB"
      stroke="#6524EB"
      strokeWidth="0.4"
    />
  </svg>
);

export const StarIcon = (p) => (
  <svg {...stroke({ fill: "currentColor", strokeWidth: 1.2, ...p })}>
    <path d="M12 3.6l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.8l5.9-.9z" />
  </svg>
);

export const PeopleIcon = (p) => (
  <svg {...stroke(p)}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
    <path d="M16.5 6.3a3 3 0 0 1 0 5.6M18 19.5c0-2.2-.9-3.9-2.4-4.7" />
  </svg>
);

export const ShareIcon = (p) => (
  <svg {...stroke({ width: 17, height: 17, ...p })}>
    <circle cx="18" cy="5.5" r="2.5" />
    <circle cx="6" cy="12" r="2.5" />
    <circle cx="18" cy="18.5" r="2.5" />
    <path d="M8.2 10.8l7.6-4M8.2 13.2l7.6 4" />
  </svg>
);

// The "How it works" badge: a dashed orbit ring, a gradient disc, the shield
// with its tick, and two loose dots. Self-contained rather than a plain shield
// on a CSS circle, so the ring's dashes and the gradient cannot drift.
export const HowItWorksBadge = (p) => (
  <svg viewBox="0 0 74 76" width="74" height="76" fill="none" aria-hidden="true" {...p}>
    <ellipse cx="37" cy="37.77" rx="36.5" ry="37.5" stroke="#CCB4FF" strokeWidth="0.5" strokeDasharray="1 1" />
    <ellipse cx="37.0006" cy="37.7734" rx="29.2917" ry="30.0625" fill="url(#rx_hiw_grad)" />
    <path
      d="M37.6358 21.5947C37.5301 21.6054 37.4264 21.6302 37.3274 21.6683C33.5446 23.1739 29.545 24.6659 22.7328 24.881C22.4252 24.8905 22.1334 25.0188 21.9192 25.2385C21.705 25.4582 21.5853 25.7522 21.5855 26.0581C21.5822 27.3676 21.5854 34.7481 21.5855 35.9535C21.5856 42.639 23.6447 47.4188 26.6929 50.7169C29.7412 54.015 33.7034 55.8155 37.4507 56.8601C37.6605 56.9188 37.8825 56.9188 38.0922 56.8601C41.8396 55.8155 45.8017 54.015 48.85 50.7169C51.8983 47.4188 53.9573 42.639 53.9575 35.9535C53.9575 34.7481 53.9608 27.3676 53.9575 26.0581C53.9576 25.7522 53.8379 25.4582 53.6238 25.2385C53.4096 25.0188 53.1178 24.8905 52.8102 24.881C45.998 24.6659 41.9983 23.1739 38.2156 21.6683C38.0316 21.596 37.8322 21.5707 37.6358 21.5947ZM37.7715 24.0349C41.2725 25.4164 45.4134 26.7886 51.5888 27.1372C51.5906 29.3306 51.5888 34.8747 51.5888 35.9535C51.5887 42.1693 49.7546 46.2621 47.1105 49.1228C44.5482 51.8951 41.149 53.4999 37.7715 54.4813C34.394 53.4999 30.9947 51.8951 28.4324 49.1228C25.7883 46.2621 23.9543 42.1693 23.9541 35.9535C23.9541 34.8747 23.9524 29.3306 23.9541 27.1372C30.1295 26.7886 34.2705 25.4164 37.7715 24.0349ZM45.2106 32.5692C44.9038 32.5877 44.6163 32.724 44.4087 32.9493L35.045 42.9673L31.1712 38.4794C31.0702 38.3611 30.9467 38.2638 30.8077 38.1931C30.6687 38.1224 30.5171 38.0796 30.3615 38.0673C30.2058 38.055 30.0493 38.0733 29.9008 38.1213C29.7523 38.1692 29.6148 38.2459 29.4963 38.3468C29.3777 38.4477 29.2803 38.5709 29.2097 38.7094C29.1392 38.8478 29.0968 38.9987 29.0851 39.1534C29.0733 39.3082 29.0925 39.4637 29.1414 39.6111C29.1903 39.7584 29.268 39.8947 29.37 40.0122L34.1074 45.5056C34.2159 45.6311 34.3498 45.7326 34.5003 45.8032C34.6509 45.8739 34.8147 45.9122 34.9812 45.9157C35.1477 45.9192 35.313 45.8878 35.4665 45.8235C35.6199 45.7591 35.7579 45.6634 35.8716 45.5425L46.1359 34.5558C46.2994 34.3847 46.4075 34.1688 46.4461 33.936C46.4847 33.7032 46.452 33.4642 46.3522 33.2501C46.2525 33.036 46.0903 32.8566 45.8868 32.7352C45.6832 32.6138 45.4476 32.5559 45.2106 32.5692Z"
      fill="white"
      stroke="white"
      strokeWidth="0.5"
    />
    <circle cx="59.3542" cy="8.47917" r="3.85417" fill="#6524EB" />
    <circle cx="10.0202" cy="62.4401" r="3.85417" fill="#6524EB" />
    <defs>
      <linearGradient id="rx_hiw_grad" x1="37.0006" y1="7.71094" x2="49.334" y2="67.8359" gradientUnits="userSpaceOnUse">
        <stop stopColor="#9B6BFF" />
        <stop offset="1" stopColor="#5308EE" />
      </linearGradient>
    </defs>
  </svg>
);

export const LinkIcon = (p) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
    <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
  </svg>
);

export const CopyIcon = (p) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </svg>
);

// Bare tick, no ring: for the green status disc, where the circle is already
// drawn in CSS and CheckCircleIcon's own outline would sit inside it as a
// second, thinner circle.
export const CheckIcon = (p) => (
  <svg {...stroke({ width: 16, height: 16, strokeWidth: 3, ...p })}>
    <path d="M5.5 12.5l4 4 9-9.5" />
  </svg>
);

export const CheckCircleIcon = (p) => (
  <svg {...stroke({ width: 15, height: 15, ...p })}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </svg>
);

export const ShieldIcon = (p) => (
  <svg {...stroke({ width: 15, height: 15, ...p })}>
    <path d="M12 3l7 3v5.5c0 4.3-2.9 7.7-7 9.5-4.1-1.8-7-5.2-7-9.5V6z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

export const UploadIcon = (p) => (
  <svg {...stroke({ width: 22, height: 22, ...p })}>
    <path d="M12 16V4M8.5 7.5L12 4l3.5 3.5" />
    <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
  </svg>
);

export const CoinIcon = (p) => (
  <svg {...stroke({ width: 13, height: 13, ...p })}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5v9M9.8 9.8h3.4a1.7 1.7 0 0 1 0 3.4h-2.4a1.7 1.7 0 0 0 0 3.4h3.4" />
  </svg>
);

/**
 * The gift box that anchors the Give Feedback banner: box, a lid tipped off it
 * at an angle, a two-loop bow and a few sparkles. Drawn rather than shipped as
 * an image so it stays crisp at any size and carries no extra request on a
 * screen that already waits on the feedback API.
 *
 * Fixed brand purples rather than currentColor: the tilted lid and the front
 * ribbon only read as depth if they are three distinct shades, which a single
 * inherited colour cannot give.
 */
export const GiftIcon = ({ width = 100, height = 100 }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 100 100"
    fill="none"
    aria-hidden="true"
  >
    {/* box, with the front ribbon a shade darker so the face has a seam */}
    <rect x="42" y="48" width="48" height="42" rx="5" fill="#7C3AED" />
    <rect x="60" y="48" width="9" height="42" fill="#5B18CE" opacity="0.85" />

    {/* lid, tipped up off the box the way a just-opened one sits */}
    <g transform="rotate(-22 66 44)">
      <rect x="36" y="34" width="58" height="15" rx="4" fill="#9B6BFF" />
      <rect x="57" y="34" width="9" height="15" fill="#7C3AED" />
    </g>

    {/* bow: two loops, outlined rather than filled, as in the design */}
    <g stroke="#6D28D9" strokeWidth="4.5" fill="none">
      <ellipse cx="41" cy="23" rx="7.5" ry="5" transform="rotate(-35 41 23)" />
      <ellipse cx="54" cy="29" rx="7.5" ry="5" transform="rotate(35 54 29)" />
    </g>

    {/* sparkles */}
    <path
      d="M88 16v11M82.5 21.5h11"
      stroke="#6D28D9"
      strokeWidth="3.4"
      strokeLinecap="round"
    />
    <circle cx="78" cy="10" r="2.6" fill="#6D28D9" />
    <circle cx="94" cy="34" r="2.2" fill="#6D28D9" />
  </svg>
);

// ── Brand marks ────────────────────────────────────────────────────────────
// Simplified but recognisable: each keeps the mark's silhouette and colour so a
// row is identifiable at a glance, without shipping the full logo artwork.

// G2's mark ships as artwork rather than a path — the brand's own export is a
// raster, and hand-tracing it would only approximate a trademark. Served from
// /public so it is fetched once and cached, instead of inlining ~120KB of
// base64 into the bundle for an 18px icon.
export const G2Logo = (p) => (
  <img
    src="/images/g2_logo.png"
    alt=""
    aria-hidden="true"
    width={18}
    height={18}
    style={{ objectFit: "contain" }}
    {...p}
  />
);

// The real Capterra mark: four wedges, no wordmark — the brand's own artwork
// cropped to the glyph, since at 18px the lettering would be unreadable anyway.
export const CapterraLogo = () => (
  <svg viewBox="0 0 330 344.983" width="18" height="18" aria-hidden="true">
    <path fill="#FF9D28" d="M8.905 127.38l135.181.027 82.199.014V45.987z" />
    <path fill="#68C5ED" d="M226.285 45.987V333.49L328.95 7.534z" />
    <path fill="#044D80" d="M226.285 127.421l-82.199-.014 82.199 206.083z" />
    <path fill="#E54747" d="M8.905 127.38l156.26 52.905-21.079-52.878z" />
  </svg>
);

// The real Play mark, gradients and all. Gradient ids carry the export's own
// suffix so they cannot collide with another inline SVG on the page — an id
// clash would silently repaint whichever element resolved it second.
export const PlayLogo = () => (
  <svg viewBox="0 0 30 33" width="18" height="18" fill="none" aria-hidden="true">
    <path d="M0.549878 0.285156C0.383137 0.405636 0.24667 0.563211 0.151243 0.745453C0.0558168 0.927694 0.00404097 1.12962 0 1.33529V30.8477C0 31.2862 0.225316 31.6577 0.55122 31.9018L17.0999 16.0948L0.549878 0.285156Z" fill="#29B6F6" />
    <path d="M0.549878 0.285156C0.383137 0.405636 0.24667 0.563211 0.151243 0.745453C0.0558168 0.927694 0.00404097 1.12962 0 1.33529V30.8477C0 31.2862 0.225316 31.6577 0.55122 31.9018L17.0999 16.0948L0.549878 0.285156Z" fill="url(#rx_play_a)" />
    <path d="M22.1962 11.2305C15.4126 7.46186 6.9874 2.84824 1.96071 0.159204C1.47253 -0.102324 0.938743 -0.00307759 0.549805 0.289297L17.0998 16.099L22.1962 11.2305Z" fill="#00E676" />
    <path d="M22.1962 11.2305C15.4126 7.46186 6.9874 2.84824 1.96071 0.159204C1.47253 -0.102324 0.938743 -0.00307759 0.549805 0.289297L17.0998 16.099L22.1962 11.2305Z" fill="url(#rx_play_b)" />
    <path d="M0.550781 31.9046C0.775587 32.0842 1.0531 32.185 1.34073 32.1917C1.55353 32.1917 1.76051 32.138 1.96169 32.0307C6.98704 29.3417 15.4096 24.7281 22.1905 20.9621L17.0981 16.0977L0.550781 31.9046Z" fill="#F44336" />
    <path d="M0.550781 31.9046C0.775587 32.0842 1.0531 32.185 1.34073 32.1917C1.55353 32.1917 1.76051 32.138 1.96169 32.0307C6.98704 29.3417 15.4096 24.7281 22.1905 20.9621L17.0981 16.0977L0.550781 31.9046Z" fill="url(#rx_play_c)" />
    <path d="M28.8227 14.9349C26.6186 13.6902 24.4097 12.4541 22.196 11.2266L17.0996 16.095L22.192 20.9594C24.4075 19.731 26.6182 18.494 28.8241 17.2484C29.2452 17.0097 29.5067 16.5671 29.5067 16.091C29.5067 15.6149 29.2452 15.1723 28.8241 14.9349" fill="#FFEA00" />
    <path d="M28.8227 14.9349C26.6186 13.6902 24.4097 12.4541 22.196 11.2266L17.0996 16.095L22.192 20.9594C24.4075 19.731 26.6182 18.494 28.8241 17.2484C29.2452 17.0097 29.5067 16.5671 29.5067 16.091C29.5067 15.6149 29.2452 15.1723 28.8241 14.9349" fill="url(#rx_play_d)" />
    <path d="M1.86633 31.7005C8.81357 28.0123 22.2172 20.6908 28.9284 16.9208C29.1657 16.7847 29.3557 16.5792 29.4729 16.332C29.4383 16.5241 29.3621 16.7063 29.2498 16.8659C29.1374 17.0255 28.9916 17.1587 28.8224 17.2561C22.1622 21.0261 8.85649 28.3475 1.96155 32.0358C1.13808 32.4757 0.183166 31.9124 0.0302734 31.0808C0.345448 31.7152 1.15417 32.0787 1.86633 31.7005Z" fill="black" fillOpacity="0.0627451" />
    <path d="M28.823 14.9324C27.0178 13.9104 6.98882 2.84043 1.96213 0.151391C1.4726 -0.110136 0.938817 -0.0108901 0.549878 0.281484C0.383137 0.401964 0.24667 0.559539 0.151243 0.741781C0.0558168 0.924022 0.00404097 1.12594 0 1.33162V30.8467C0 31.2839 0.225316 31.6567 0.55122 31.9008C0.776025 32.0804 1.05354 32.1812 1.34117 32.1878C1.55397 32.1878 1.76095 32.1342 1.96213 32.0269C6.98748 29.3379 27.0165 18.2665 28.8217 17.2459C29.0272 17.1301 29.1986 16.9621 29.3184 16.7589C29.4383 16.5556 29.5024 16.3244 29.5043 16.0885C29.5022 15.8528 29.4379 15.6218 29.3181 15.4189C29.1982 15.2159 29.0284 15.0481 28.823 14.9324Z" fill="url(#rx_play_e)" />
    <defs>
      <linearGradient id="rx_play_a" x1="8.54994" y1="2.80789" x2="8.54994" y2="24.1459" gradientUnits="userSpaceOnUse">
        <stop stopColor="#00B8D4" stopOpacity="0" />
        <stop offset="1" stopColor="white" stopOpacity="0.3" />
      </linearGradient>
      <linearGradient id="rx_play_b" x1="0.549805" y1="0.0078125" x2="22.1962" y2="0.0078125" gradientUnits="userSpaceOnUse">
        <stop stopColor="white" stopOpacity="0.2" />
        <stop offset="1" stopColor="white" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="rx_play_c" x1="8.69703" y1="23.7597" x2="14.0456" y2="29.1083" gradientUnits="userSpaceOnUse">
        <stop stopOpacity="0.1" />
        <stop offset="1" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="rx_play_d" x1="20.3372" y1="12.8561" x2="26.811" y2="19.3299" gradientUnits="userSpaceOnUse">
        <stop stopOpacity="0.05" />
        <stop offset="1" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="rx_play_e" x1="0" y1="0" x2="29.5043" y2="0" gradientUnits="userSpaceOnUse">
        <stop stopColor="white" stopOpacity="0.2" />
        <stop offset="1" stopColor="white" stopOpacity="0" />
      </linearGradient>
    </defs>
  </svg>
);

export const TrustpilotLogo = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path
      d="M12 2.4l2.8 6.1 6.6.7-4.9 4.5 1.3 6.5L12 17l-5.8 3.2 1.3-6.5-4.9-4.5 6.6-.7z"
      fill="#00B67A"
    />
  </svg>
);

export const ProductHuntLogo = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <circle cx="12" cy="12" r="11" fill="#DA552F" />
    <path
      d="M10 6.8h3.6a3.1 3.1 0 0 1 0 6.2H10V6.8zm0 6.2V17"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const AppSumoLogo = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <circle cx="12" cy="12" r="11" fill="#FFC900" />
    <path
      d="M7.5 15.5l4.5-9 4.5 9"
      fill="none"
      stroke="#1c1d26"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
